import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Star,
  Heart,
  Send,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionFeedback = ({ requestId, responderName, onComplete }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [wasHelpful, setWasHelpful] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/requests/${requestId}/feedback`, {
        rating,
        feedback: feedback.trim() || null,
        was_helpful: wasHelpful
      });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
      if (onComplete) onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="card-valor">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
            Thank You!
          </h3>
          <p className="text-muted-foreground">
            Your feedback helps us improve VALORLink for all veterans.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-valor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Heart className="w-5 h-5 text-secondary" />
          Rate Your Session
        </CardTitle>
        <CardDescription>
          How was your experience with {responderName || "your responder"}?
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Star Rating */}
        <div className="space-y-2">
          <Label>Overall Rating</Label>
          <div className="flex gap-2 justify-center py-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="focus:outline-none transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoveredRating || rating)
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground">
            {rating === 0 && "Tap to rate"}
            {rating === 1 && "Poor"}
            {rating === 2 && "Fair"}
            {rating === 3 && "Good"}
            {rating === 4 && "Very Good"}
            {rating === 5 && "Excellent"}
          </p>
        </div>

        {/* Was Helpful Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <Label htmlFor="helpful">Was this session helpful?</Label>
            <p className="text-sm text-muted-foreground">
              Let us know if you got the support you needed
            </p>
          </div>
          <Switch
            id="helpful"
            checked={wasHelpful}
            onCheckedChange={setWasHelpful}
          />
        </div>

        {/* Written Feedback */}
        <div className="space-y-2">
          <Label htmlFor="feedback">Additional Comments (Optional)</Label>
          <Textarea
            id="feedback"
            placeholder="Share any additional thoughts about your experience..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="min-h-[100px]"
            data-testid="feedback-textarea"
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || rating === 0}
          className="w-full"
          data-testid="submit-feedback-btn"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Your feedback is anonymous and helps improve our service
        </p>
      </CardContent>
    </Card>
  );
};

export default SessionFeedback;
